/* eslint-disable no-unused-vars */
import { useNavigation } from '@react-navigation/native';
import { useStoreActions, useStoreState } from '@/stores/store';
import { useCallback, useEffect, useState } from 'react';
import { Platform, View } from 'react-native';
import { Text } from '@/components/Text';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import {
  fetchPatientsList,
  permission as permissionAPI,
} from '../../api/services/user';
import colors, { appTheme } from '@/config/colors';
import { useDashboardContext } from '@/contexts/DashboardContext/useDashboardContext';
import encryptedStorage from '../../stores/encryptedStorage';
import { Wrapper } from './Wrapper';
import { hasInternetAsync } from './helpers';
import { Icon, IconName } from '@/components/Icon';
import { IconName as NeolexonIconName } from '@/components/NeolexonIcon/Icons/types';
import DownloadManager from '../../utils/DownloadManager';
import { styles } from './styles';
import { logError } from '@/utils/logHandling';
import { WordSet } from '@/stores/store.types';
import { StackNavigationProp } from '@react-navigation/stack';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { isExerciseSet } from '@/utils/isExerciseSet';
import { Toggle } from '@/components/ui/therapist/toggle';
import { Modal } from '@/components/ui/therapist/Modal';
import { Button } from '@/components/ui/therapist/Button';
import { isNeolexonIcon } from '@/components/NeolexonIcon/Icons';
import { NeolexonIcon } from '@/components/NeolexonIcon';

const iconColors = {
  play: appTheme.primary,
  image: colors.darkPurpleBlue,
  default: colors.darkGrey,
};

export const API_NAME_ACCORD_TYPE = {
  Wörtersets: 'wordsets',
  Sätzesets: 'sentencesets',
  Textesets: 'textsets',
};

export const SCREEN_NAME_ACCORD_TYPE = {
  Wörtersets: 'WordSet',
  Sätzesets: 'SentenceSet',
  Textesets: 'TextSet',
};

type Option = {
  name: string;
  isDisabled?: boolean;
  component: React.ReactElement | null;
  icon: IconName | NeolexonIconName | null;
  onPress?: () => void;
};

interface SetCardOptionsProps {
  _id: string;
  patientsSyncing: Function;
  type: string;
  exerciseSetName: string;
  indexSetCard: number;
  appDisplayName: string;
}

export function SetCardOptions({
  _id,
  patientsSyncing,
  type,
  exerciseSetName,
  indexSetCard,
  appDisplayName,
}: SetCardOptionsProps) {
  const { navigate } =
    useNavigation<StackNavigationProp<TherapistStackParamList>>();

  /**
   * isSync is the state indicating whether the set should be downloaded or not. It also represents the state of the "laden" switch
   * isLoaded is the state indicating whether the set is downloaded on the platforme or not.
   * In order:
   * - we turn isSync to true when the switch is "on"
   * - the DashboardContext fetches patient and downloads the set and assets
   * - when the all is downloaded, we can start the exercise.
   */
  const [isSync, setIsSync] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [ergebnisseTherapistDemo, setErgebnisseTherapistDemo] = useState(false);
  const { permissions, isTherapistDemo, refresh, toolTipNumber } =
    useStoreState((state) => state);

  const { setGlobalPopupState, onFirstMountStoreSetup, setToolTipNumber } =
    useStoreActions((actions) => ({
      setGlobalPopupState: actions.setGlobalPopupState,
      onFirstMountStoreSetup: actions.onFirstMountStoreSetup,
      setToolTipNumber: actions.setToolTipNumber,
    }));

  const {
    fetchPatients,
    selectedID,
    selectedPatient,
    setShowStorageWarningModal,
    downloadedExercises,
  } = useDashboardContext();
  const { showNotification } = useNotification();

  const toggleErgebnisseTherapistDemo = useCallback(() => {
    setErgebnisseTherapistDemo((prev) => !prev);
  }, []);

  const revertLocalSetStructure = async (setId: string) => {
    let localSet = await encryptedStorage().getItem(setId);

    if (isExerciseSet(localSet)) {
      let {
        activeWordset,
        assets,
        videosAdded,
        ablenkerFromSetJson,
        ...whatWeNeed
      } = localSet;
      whatWeNeed.preparedSet = {};
      whatWeNeed.preparedSet.date = '';
      await encryptedStorage().setItem(setId, whatWeNeed);
    } else {
      logError(
        `Cannot revert set structure, not an exercise set with id ${setId}`,
      );
    }
  };

  const setLocalSetSync = async (setId: string, value: boolean) => {
    let localSet = await encryptedStorage().getItem(setId);
    if (localSet) {
      await encryptedStorage().setItem(setId, { ...localSet, sync: value });
    }
  };

  const handleLoadChange = async (newValue: boolean) => {
    if (isTherapistDemo) {
      if (newValue === true && toolTipNumber === 1) {
        setToolTipNumber(2);
      }
      if (newValue === false && toolTipNumber === 2) {
        setToolTipNumber(1);
      }
    }

    if (newValue) {
      const hasInternet = await hasInternetAsync();
      const hasFreeSpace = await DownloadManager.hasFreeStorage();

      if (!hasInternet) {
        setIsSync(false);
        setGlobalPopupState({
          headerText: 'Keine Verbindung zum neolexon Server',
          bodyText:
            'Überprüfen Sie Ihre Internetverbindung und versuchen es erneut.',
          leftButton: '',
          rightButton: '',
        });
      } else if (!hasFreeSpace) {
        setIsSync(false);
        setShowStorageWarningModal(true);
      } else {
        await setLocalSetSync(_id, newValue);
        await patientsSyncing();
        setIsSync(true);
      }
    } else {
      await setLocalSetSync(_id, newValue);
      await revertLocalSetStructure(_id);
      await patientsSyncing();
      setIsSync(false);
    }
  };

  useEffect(
    () => {
      encryptedStorage()
        .getItem(_id)
        .then((res) => {
          setIsSync(res?.sync);
          setIsLoaded(res?.activeWordset ? true : false);
        });
    },
    // We use this refresh dependency to ensure that the component updates when the eigentraining is loaded.
    [refresh, downloadedExercises],
  );
  const options = () => {
    let config: Option[] = [];

    const isDisabled = !isSync || !isLoaded;

    const ergebnisse: Option = {
      name: 'Ergebnisse',
      component: null,
      icon: 'barChart',
      onPress: () => {
        let className = '';
        let classSet = '';
        if (type == 'Wörtersets') {
          classSet = 'Wörterset';
          className = 'wordset';
        }
        if (type == 'Sätzesets') {
          classSet = 'Sätzeset';
          className = 'sentenceset';
        }
        if (isTherapistDemo) {
          toggleErgebnisseTherapistDemo();
        } else {
          // @ts-ignore
          navigate('Ergebnisse', {
            exerciseSetName,
            exerciseSet: classSet,
            exerciseClass: className,
            setId: _id,
            patientName:
              selectedPatient.firstName + ' ' + selectedPatient.lastName,
            patientId: selectedID,
            appDisplayName: appDisplayName,
          });
        }
      },
    };

    const laden: Option = {
      name: 'Laden',
      icon: null,
      component: (
        <Toggle
          value={isSync}
          onToggle={(value) => {
            setIsSync(value);
            handleLoadChange(value);
          }}
        />
      ),
    };

    const textAufgaben: Option = {
      isDisabled,
      name: 'Aufgaben',
      component: null,
      icon: 'playCircle',
      onPress: async () => {
        let className = '';
        if (type == 'Wörtersets') {
          className = 'wordset';
        }
        if (type == 'Sätzesets') {
          className = 'sentenceset';
        }
        if (type == 'Textesets') {
          className = 'textset';
        }
        const exerciseSettings = await encryptedStorage().getItem(_id);
        // @ts-ignore
        navigate('Aufgaben', {
          exerciseClass: className,
          patientId: 'NOT_SELF',
          setId: _id,
          exerciseSettings,
        });
      },
    };

    const otherAufgaben: Option = {
      isDisabled,
      name: 'Aufgaben',
      component: null,
      icon: 'playCircle',
      onPress: async () => {
        let className = '';
        if (type == 'Wörtersets') {
          className = 'wordset';
        }
        if (type == 'Sätzesets') {
          className = 'sentenceset';
        }
        if (type == 'Textesets') {
          className = 'textset';
        }
        if (isTherapistDemo && toolTipNumber === 2) {
          setToolTipNumber(4);
        }
        const exerciseSettings = await encryptedStorage().getItem(_id);
        // @ts-ignore
        navigate('Aufgaben', {
          exerciseClass: className,
          patientId: selectedID,
          isSelf: false,
          setId: _id,
          exerciseSettings,
        });
      },
    };

    const createTemplate: Option = {
      name: 'Als Vorlage speichern',
      isDisabled: false,
      component: null,
      icon: 'bookShelf',
      onPress: () => {
        navigate(SCREEN_NAME_ACCORD_TYPE[type], {
          selectedID: selectedID,
          setId: _id,
          apiName: API_NAME_ACCORD_TYPE[type],
          templateMode: 'create',
        });
      },
    };

    const neueAblenker: Option = {
      name: 'Neue Ablenker',
      isDisabled,
      component: null,
      icon: 'refresh',
      onPress: async () => {
        try {
          const hasInternet = await hasInternetAsync();
          if (hasInternet) {
            const res = await encryptedStorage().getItem(_id);
            const { activeWordset, ...rest } = res as WordSet;
            await encryptedStorage().setItem(_id, rest);
            const objFetchPatientList = await fetchPatientsList(true);
            await fetchPatients(selectedPatient, objFetchPatientList.patients);
            await patientsSyncing(objFetchPatientList.patients, true);
            const objPermission = await permissionAPI(true);
            showNotification({
              type: 'info',
              title: 'Neue Ablenker wurden generiert.',
            });
            onFirstMountStoreSetup({
              appStartResumeLock: objFetchPatientList.appStartResumeLock,
              eigentrainingLock: objFetchPatientList.eigentrainingLock,
              appLockPattern: objFetchPatientList.appLockPattern,
              patients: objFetchPatientList.patients,
              subscriptions: objPermission.subscriptions,
              permissions: objPermission.permissions,
            });
          } else {
            setGlobalPopupState({
              headerText: 'Keine Verbindung zum neolexon Server',
              bodyText:
                'Überprüfen Sie Ihre Internetverbindung und versuchen es erneut.',
              leftButton: '',
              rightButton: '',
            });
          }
        } catch (error) {
          logError('Neue Ablenker in SetCardOptions ', error);
        }
      },
    };

    const bildkarten: Option = {
      name: 'Bildkarten',
      isDisabled,
      component: null,
      icon: 'image',
      onPress: async () => {
        let className = '';
        if (type == 'Wörtersets') {
          className = 'wordset';
        }
        if (type == 'Sätzesets') {
          className = 'sentenceset';
        }
        if (type == 'Textesets') {
          className = 'textset';
        }
        const data = await encryptedStorage().getItem(_id);
        if (isExerciseSet(data)) {
          // @ts-ignore
          navigate('BildKarten', {
            exerciseClass: className,
            setId: _id,
            cardSettings: data.cardSettings,
          });
        } else {
          logError(
            `Cannot navigate to BildKarten, not an exercise set with id ${_id}`,
          );
        }
      },
    };

    // Only for web in the case of too many connexions
    let canStartTraining = true;
    if (Platform.OS === 'web') {
      const currentUrlParams = new URLSearchParams(window.location.search);
      canStartTraining =
        currentUrlParams.get('nur-eigentraining-einstellen') === null;
    }

    if (!canStartTraining) {
      if (type == 'Textesets') {
        config = [];
      } else {
        config = [createTemplate, ergebnisse];
      }
    } else {
      if (type == 'Textesets') {
        config = [laden, textAufgaben];
      } else {
        config = [
          laden,
          neueAblenker,
          bildkarten,
          otherAufgaben,
          createTemplate,
          ergebnisse,
        ];
      }
    }
    return config;
  };

  const optionsRenderer = () => {
    return options().map((option: Option, index: number) => {
      const isLastIndex = index === options().length - 1;

      const color =
        option.icon && option.icon in iconColors
          ? iconColors[option.icon as keyof typeof iconColors]
          : iconColors.default;

      const renderedOption = option.icon ? (
        isNeolexonIcon(option.icon) ? (
          <NeolexonIcon
            name={option.icon}
            size={28}
            color={colors.newUI.therapist.primaryBlue80}
            fillColor="none"
          />
        ) : (
          <Icon name={option.icon} size={24} color={color} />
        )
      ) : (
        option.component
      );

      const Children = (
        <View style={[styles.setCardOptionWrapper]}>
          <View key={option.name} style={styles.setCardOption}>
            <Text style={styles.cardOptionName}>{option.name}</Text>
            {renderedOption}
          </View>
        </View>
      );

      if (
        option.name === 'Ergebnisse' ||
        option.name === 'Als Vorlage speichern'
      ) {
        return (
          <Wrapper
            isDisabled={option.isDisabled}
            onPress={option.onPress}
            isLast={isLastIndex}
            key={option.name}
          >
            {Children}
          </Wrapper>
        );
      } else {
        if (permissions?.exercises || permissions?.pictureCards) {
          if (
            isTherapistDemo &&
            type === 'Wörtersets' &&
            option.name === 'Laden' &&
            !isSync &&
            toolTipNumber === 1 &&
            indexSetCard === 0
          ) {
            return (
              <Wrapper
                isDisabled={option.isDisabled}
                onPress={option.onPress}
                isLast={isLastIndex}
                key={option.name}
              >
                {Children}
              </Wrapper>
            );
          } else if (
            isTherapistDemo &&
            type === 'Wörtersets' &&
            option.name === 'Aufgaben' &&
            isSync &&
            toolTipNumber === 2 &&
            indexSetCard === 0
          ) {
            return (
              <Wrapper
                isDisabled={option.isDisabled}
                onPress={option.onPress}
                isLast={isLastIndex}
                key={option.name}
              >
                {Children}
              </Wrapper>
            );
          } else {
            return (
              <Wrapper
                isDisabled={option.isDisabled}
                onPress={option.onPress}
                isLast={isLastIndex}
                key={option.name}
              >
                {Children}
              </Wrapper>
            );
          }
        } else {
          return null;
        }
      }
    });
  };

  useEffect(() => {
    // sometimes the tooltipnumber is false, which is strange since in the store itself it's set to 1. So just in case we have this code
    if (isTherapistDemo && (toolTipNumber as unknown) === false) {
      setToolTipNumber(1);
    }
  }, [isSync]);

  return (
    <View style={styles.setCardOptions}>
      {isTherapistDemo ? (
        <Modal
          onClose={() => setErgebnisseTherapistDemo(false)}
          isVisible={ergebnisseTherapistDemo}
          title="Ergebnisse in der Demo nicht verfügbar"
          confirmActionButton={
            <Button label="OK" onPress={toggleErgebnisseTherapistDemo} />
          }
        >
          <Text style={{ fontSize: 18 }}>
            Registrieren Sie sich bei neolexon, um Ihre eigenen Patient:innen
            und Übungssets zu erstellen.
          </Text>
        </Modal>
      ) : null}
      {optionsRenderer()}
    </View>
  );
}
